// Structured VoIP call events sent to Kibana (admin_cn/report), modeled on the mobile
// VoipKibanaLogger. Field names inside `voip` match iOS/Android so dashboards can be shared.
// Only identifiers, codes and fixed reason values are sent; vendor error messages are not.

export const VOIP_LOG_MESSAGE = 'voip';
export const VOIP_STATS_INTERVAL_MS = 5000;

export type VoipPhase =
  | 'incoming_received'
  | 'answered_elsewhere'
  | 'initiation'
  | 'initiation_success'
  | 'answering'
  | 'connecting'
  | 'established'
  | 'reconnecting'
  | 'reconnected'
  | 'participant_added'
  | 'participant_removed'
  | 'participant_left'
  | 'mute_toggled'
  | 'network_quality_changed'
  | 'failed'
  | 'busy'
  | 'call_end'
  | 'call_stats';

export type VoipCall = {
  callId?: string;
  isIncoming?: boolean;
  memberIds?: string[];
  organizationId?: string;
  payload?: {
    callId?: string;
    callerId?: string;
    isVideo?: boolean;
    orgId?: string;
    recipientToken?: string;
  };
  room?: { name?: string; sid?: string; participants?: { size: number } } | null;
  roomName?: string;
};

// Who or what ended the call. `network` covers Twilio signaling/media connection failures.
export type VoipEndedBy =
  | 'local_user'
  | 'remote'
  | 'room_completed'
  | 'network'
  | 'tab_closed'
  | 'duplicate_identity'
  | 'token_expired'
  | 'error';

export type VoipDiagnostics = {
  callDuration?: number;
  connectionDownlink?: number;
  connectionEffectiveType?: string;
  connectionRtt?: number;
  lastNetworkQuality?: number | null;
  lastStats?: VoipCallStats;
  minNetworkQuality?: number | null;
  online?: boolean;
  reconnectCount?: number;
  remainingParticipants?: number;
};

export type VoipEventDetails = {
  callEndCause?: string;
  callState?: string;
  code?: number | string;
  diagnostics?: VoipDiagnostics;
  disconnectReason?: string;
  endedBy?: VoipEndedBy;
  lastParticipantLeft?: boolean;
  mute?: boolean;
  networkQuality?: number | null;
  participantId?: string;
  remote?: boolean;
  stats?: VoipCallStats;
};

// https://www.twilio.com/docs/api/errors (Video 53xxx)
const ROOM_COMPLETED_CODES = [53118];
const NETWORK_ERROR_CODES = [
  53000, // Signaling connection error
  53001, // Signaling connection disconnected
  53002, // Signaling connection timed out
  53405, // Media connection failed
  53407, // Media connection failed due to DTLS handshake failure
];
const DUPLICATE_IDENTITY_CODES = [53205];
const TOKEN_EXPIRED_CODES = [20104];

// Maps a Twilio room disconnect to fixed values. `code` carries the detail; the Twilio
// message is not logged.
export function classifyRoomDisconnect(error?: { code?: number } | null): {
  endedBy: VoipEndedBy;
  disconnectReason: string;
} {
  const code = error?.code;
  let endedBy: VoipEndedBy;
  if (!error) {
    // A clean disconnect we didn't start (user ends are logged before the room disconnects):
    // the SDK leaves the room when the call ends remotely.
    return { endedBy: 'remote', disconnectReason: 'disconnected' };
  } else if (code && ROOM_COMPLETED_CODES.includes(code)) {
    endedBy = 'room_completed';
  } else if (code && NETWORK_ERROR_CODES.includes(code)) {
    endedBy = 'network';
  } else if (code && DUPLICATE_IDENTITY_CODES.includes(code)) {
    endedBy = 'duplicate_identity';
  } else if (code && TOKEN_EXPIRED_CODES.includes(code)) {
    endedBy = 'token_expired';
  } else {
    endedBy = 'error';
  }
  return { endedBy, disconnectReason: endedBy };
}

type NetworkInformation = { downlink?: number; effectiveType?: string; rtt?: number };

export function getBrowserNetworkInfo(): VoipDiagnostics {
  if (typeof navigator === 'undefined') return {};
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  return {
    online: navigator.onLine,
    connectionDownlink: connection?.downlink,
    connectionEffectiveType: connection?.effectiveType,
    connectionRtt: connection?.rtt,
  };
}

// Phases that usually precede the tab closing; flushed so they aren't lost in the batch.
const FLUSH_IMMEDIATELY_PHASES: VoipPhase[] = ['call_end', 'failed'];

type SendFn = (args: {
  flushImmediately?: boolean;
  level: string;
  message: string;
  organizationId?: string;
  payload: Record<string, unknown>;
}) => void;

export type VoipLoggerDeps = {
  currentUserId?: string;
  send: SendFn;
};

const getAppState = () => (typeof document !== 'undefined' ? document.visibilityState : 'unknown');

export function buildVoipPayload(
  phase: VoipPhase,
  call: VoipCall | null | undefined,
  details: VoipEventDetails = {},
  currentUserId?: string
) {
  const payload = call?.payload || {};
  const memberIds = call?.memberIds || [];
  const callerId = payload.callerId;
  const isIncoming =
    call?.isIncoming ?? (callerId && currentUserId ? callerId !== currentUserId : undefined);
  const remoteParty = memberIds.filter((id) => id !== currentUserId);
  const orgId = payload.orgId || call?.organizationId;

  return {
    appState: getAppState(),
    callEndCause: details.callEndCause,
    callerId,
    callId: payload.callId || call?.callId,
    callState: details.callState,
    code: details.code,
    ...(details.diagnostics && { diagnostics: details.diagnostics }),
    disconnectReason: details.disconnectReason,
    endedBy: details.endedBy,
    groupCall: memberIds.length > 2,
    isIncoming,
    isVideo: payload.isVideo,
    lastParticipantLeft: details.lastParticipantLeft,
    mute: details.mute,
    networkQuality: details.networkQuality,
    organization: orgId,
    orgId,
    participantId: details.participantId,
    participantsCount: call?.room?.participants
      ? call.room.participants.size + 1
      : memberIds.length || undefined,
    phase,
    remote: details.remote,
    recipientId: payload.recipientToken,
    remoteParty: remoteParty.length ? remoteParty.join(',') : undefined,
    roomId: call?.room?.sid,
    roomName: call?.roomName || call?.room?.name,
    ...(details.stats && { stats: details.stats }),
  };
}

export function logVoipEvent(
  deps: VoipLoggerDeps,
  phase: VoipPhase,
  call: VoipCall | null | undefined,
  details?: VoipEventDetails
) {
  try {
    const voip = buildVoipPayload(phase, call, details, deps.currentUserId);
    deps.send({
      flushImmediately: FLUSH_IMMEDIATELY_PHASES.includes(phase),
      level: phase === 'failed' ? 'warn' : 'info',
      message: VOIP_LOG_MESSAGE,
      organizationId: voip.orgId,
      // ReportService adds product ('wrut', like other web logs) and currentUserId itself.
      payload: { voip },
    });
  } catch (err) {
    // Logging must never break a call.
    console.error('voipKibanaLogger failed', err);
  }
}

type TrackStats = {
  bytesReceived?: number | null;
  bytesSent?: number | null;
  codec?: string | null;
  frameRate?: number | null;
  frameRateReceived?: number | null;
  frameRateSent?: number | null;
  jitter?: number | null;
  packetsLost?: number | null;
  packetsReceived?: number | null;
  packetsSent?: number | null;
  roundTripTime?: number | null;
};

export type TwilioStatsReport = {
  localAudioTrackStats?: TrackStats[];
  localVideoTrackStats?: TrackStats[];
  remoteAudioTrackStats?: TrackStats[];
  remoteVideoTrackStats?: TrackStats[];
};

export type VoipCallStats = ReturnType<typeof flattenCallStats>;

const sum = (tracks: TrackStats[], key: keyof TrackStats) =>
  tracks.reduce((total, t) => total + (typeof t[key] === 'number' ? (t[key] as number) : 0), 0);

const max = (tracks: TrackStats[], key: keyof TrackStats) => {
  const values = tracks.map((t) => t[key]).filter((v): v is number => typeof v === 'number');
  return values.length ? Math.max(...values) : undefined;
};

const firstCodec = (tracks: TrackStats[]) => tracks.find((t) => t.codec)?.codec || undefined;

export function flattenCallStats(reports: TwilioStatsReport[]) {
  const pick = (key: keyof TwilioStatsReport) => reports.flatMap((r) => r[key] || []);
  const localAudio = pick('localAudioTrackStats');
  const localVideo = pick('localVideoTrackStats');
  const remoteAudio = pick('remoteAudioTrackStats');
  const remoteVideo = pick('remoteVideoTrackStats');

  return {
    audioBytesReceived: sum(remoteAudio, 'bytesReceived'),
    audioBytesSent: sum(localAudio, 'bytesSent'),
    audioCodec: firstCodec(localAudio) || firstCodec(remoteAudio),
    audioJitter: max(remoteAudio, 'jitter'),
    audioPacketsLost: sum(remoteAudio, 'packetsLost'),
    audioPacketsReceived: sum(remoteAudio, 'packetsReceived'),
    audioPacketsSent: sum(localAudio, 'packetsSent'),
    audioRtt: max(localAudio, 'roundTripTime'),
    videoBytesReceived: sum(remoteVideo, 'bytesReceived'),
    videoBytesSent: sum(localVideo, 'bytesSent'),
    videoCodec: firstCodec(localVideo) || firstCodec(remoteVideo),
    videoFpsReceived: max(remoteVideo, 'frameRateReceived'),
    videoFpsSent: max(localVideo, 'frameRateSent'),
    videoPacketsLost: sum(remoteVideo, 'packetsLost'),
    videoRtt: max(localVideo, 'roundTripTime'),
  };
}
